import classNames from "classnames";
import { useMotionValueEvent, useScroll } from "framer-motion";
import { ReactNode, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { isProfileApproved } from "../../helperFunctions";
import { useAppSelector } from "../../redux/hooks";
import { BottomNav } from "../navs/bottomNav";
import { SideNav } from "../navs/sideNav";
import { TopNav } from "../navs/topNav";
import NetworkWarning from "../networkWarning";
import { OnboardingPopup } from "../onboarding/onboardingPopup";
import { ScrollToTop } from "../scrollToTop";
import { MobileHeader } from "./mobileHeader";

export const AppLayout = ({
  children,
  title,
  backTo,
  bodyClassName,
}: {
  children: ReactNode | ((val: { pageScrollProgress: number }) => ReactNode);
  title?: string;
  backTo?: string | "-1";
  bodyClassName?: string;
}) => {
  const [navOpen, setNavOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  const { title: urlTitle } = useParams<{ title: string }>();

  const pageRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({ container: pageRef });

  useMotionValueEvent(scrollYProgress, "change", (val) => {
    setScrollProgress(val);
  });

  const location = useLocation();

  const onboardingCompletionStatus = useAppSelector((state) => {
    return state?.auth?.onboardingCompletionStatus;
  });

  const profile = useAppSelector((state) => {
    return state.auth.profile;
  });

  return (
    <>
      <NetworkWarning />
      <ScrollToTop
        changeKey={location.pathname}
        rerunOnKeyChange
        scrollIntoView
      />

      <div
        className="
        relative !bg-light-green-bg
        flex w-screen max-w-screen h-screen max-h-[100dvh]
        transition-all overflow-x-hidden no-drag-area
        "
        style={{
          userSelect: "none",
          WebkitUserSelect: "none",
          WebkitTouchCallout: "none",
        }}
      >
        <SideNav navOpen={navOpen} setNavOpen={setNavOpen} />

        <div className="flex flex-col min-w-0 h-full flex-1">
          <MobileHeader backTo={backTo} title={urlTitle ?? title} />

          <div className="z-[99] flex-1 relative overflow-hidden flex flex-col">
            <div id="pageTopRefToScroll" className="h-0" />

            <TopNav
              navOpen={navOpen}
              setNavOpen={setNavOpen}
              className={
                !location.pathname.includes("app/overview")
                  ? "max-md:!hidden"
                  : ""
              }
              pageScrollProgress={scrollProgress}
            />

            <div
              className={classNames(
                `
                overflow-y-scroll flex-1
                app-body-px lg:!pt-10
                py-6 text-pry-dark-green-400
                !w-full md:!max-w-[full 2xl:max-w-[1500px] hide-scroll-bar
                overflow-x-hidden
                `,
                {
                  "!pb-16": !onboardingCompletionStatus?.isAllComplete,
                },
                bodyClassName
              )}
              id="appMainBody"
              ref={pageRef}
            >
              {typeof children === "function"
                ? children({
                    pageScrollProgress: scrollProgress,
                  })
                : children}
            </div>
          </div>

          <BottomNav className="md:hidden" />
        </div>

        {/* Onboarding popup */}
        {isProfileApproved(profile) &&
        !onboardingCompletionStatus?.isAllComplete ? (
          <OnboardingPopup />
        ) : undefined}
      </div>
    </>
  );
};
